"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { SiteHeader } from "@/components/layout/site-header";
import { getApiUrl } from "@/lib/api";
import styles from "./register.module.css";

type RegisterResponse = {
  message?: string;
  data?: {
    token?: string;
    member?: unknown;
    hoola?: { status?: "ready" | "failed"; message?: string };
  };
};

export function HocTapRegisterClient() {
  const router = useRouter();
  const [schools, setSchools] = useState<{ id: number; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    fetch(`${getApiUrl()}/api/v1/public/school-options`)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((res) => setSchools(res.data))
      .catch(() =>
        setMessage("Không tải được danh mục trường. Vui lòng tải lại trang."),
      );
  }, []);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    studentCode: "",
    schoolId: "",
    password: "",
    passwordConfirmation: "",
  });

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (form.password !== form.passwordConfirmation) {
      setMessage("Mật khẩu xác nhận không khớp.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    try {
      const response = await fetch(
        `${getApiUrl()}/api/v1/member/auth/register`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fullName: form.fullName.trim(),
            email: form.email.trim(),
            studentCode: form.studentCode.trim(),
            schoolId: Number(form.schoolId),
            password: form.password,
            passwordConfirmation: form.passwordConfirmation,
          }),
        },
      );
      const data = (await response.json()) as RegisterResponse;
      if (!response.ok || !data.data?.token) {
        throw new Error(data.message || "Không thể đăng ký tài khoản.");
      }

      localStorage.setItem("member_token", data.data.token);
      localStorage.setItem(
        "member_user",
        JSON.stringify(data.data.member ?? null),
      );
      window.dispatchEvent(new Event("auth-change"));
      setMessage(
        data.data.hoola?.status === "ready"
          ? "Đăng ký và đồng bộ Hoola thành công. Đang chuyển hướng..."
          : "Đăng ký thành công. Tài khoản Hoola sẽ được quản trị viên kiểm tra.",
      );
      window.setTimeout(() => router.push("/hoc-tap/dashboard"), 900);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Không thể kết nối đến máy chủ.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <SiteHeader />
      <main className={styles.main}>
        <section className={styles.panel} aria-labelledby="register-title">
          <div className={styles.heading}>
            <p>Panasonic Education</p>
            <h1 id="register-title">Đăng ký thí sinh tự do</h1>
            <span>Tạo tài khoản để truy cập cổng đào tạo trực tuyến.</span>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <label htmlFor="register-full-name">
              Họ và tên
              <input
                id="register-full-name"
                required
                minLength={2}
                value={form.fullName}
                disabled={isLoading}
                onChange={(event) =>
                  updateField("fullName", event.target.value)
                }
              />
            </label>
            <label htmlFor="register-email">
              Email
              <input
                id="register-email"
                type="email"
                required
                value={form.email}
                disabled={isLoading}
                onChange={(event) => updateField("email", event.target.value)}
              />
            </label>
            <label htmlFor="register-student-code">
              Mã sinh viên
              <input
                id="register-student-code"
                required
                minLength={2}
                value={form.studentCode}
                disabled={isLoading}
                placeholder="Nhập mã sinh viên"
                onChange={(event) =>
                  updateField("studentCode", event.target.value)
                }
              />
            </label>
            <label htmlFor="register-school-name">
              Trường theo học
              <select
                id="register-school-name"
                required
                value={form.schoolId}
                disabled={isLoading || !schools.length}
                onChange={(event) =>
                  updateField("schoolId", event.target.value)
                }
              >
                <option value="">Chọn trường theo học</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.passwords}>
              <label htmlFor="register-password">
                Mật khẩu
                <input
                  id="register-password"
                  type="password"
                  minLength={6}
                  required
                  autoComplete="new-password"
                  value={form.password}
                  disabled={isLoading}
                  onChange={(event) =>
                    updateField("password", event.target.value)
                  }
                />
              </label>
              <label htmlFor="register-password-confirmation">
                Xác nhận mật khẩu
                <input
                  id="register-password-confirmation"
                  type="password"
                  minLength={6}
                  required
                  autoComplete="new-password"
                  value={form.passwordConfirmation}
                  disabled={isLoading}
                  onChange={(event) =>
                    updateField("passwordConfirmation", event.target.value)
                  }
                />
              </label>
            </div>
            <label className={styles.terms} htmlFor="register-terms">
              <input
                id="register-terms"
                type="checkbox"
                required
                disabled={isLoading}
              />
              <span>
                Tôi đồng ý với{" "}
                <a
                  href="https://docs.google.com/document/d/1DzcrySd5lo7bIo1YzEIWNLjY2NYw6FdIij_hBObqklE/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Điều khoản sử dụng và Chính sách bảo vệ dữ liệu cá nhân của
                  Panasonic
                </a>
                .
              </span>
            </label>
            <button type="submit" disabled={isLoading} aria-busy={isLoading}>
              {isLoading ? "Đang tạo tài khoản..." : "Đăng ký tài khoản"}
            </button>
          </form>

          {message && (
            <p className={styles.message} role="status">
              {message}
            </p>
          )}
          <p className={styles.loginLink}>
            Đã có tài khoản? <Link href="/hoc-tap/login">Đăng nhập</Link>
          </p>
        </section>
      </main>
    </div>
  );
}
